import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  useToast,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Select,
  Spinner,
  Center,
  Flex,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  IconButton,
  Circle,
  useBreakpointValue,
  SimpleGrid,
  Icon,
} from '@chakra-ui/react';
import {
  FiSearch,
  FiCalendar,
  FiClock,
  FiMail,
  FiPhone,
  FiExternalLink,
  FiChevronDown,
  FiChevronUp,
  FiX,
} from 'react-icons/fi';

import { getAllAppointments } from 'services/appointmentBooking';

const APPT_STATUS = {
  pending:   { label: 'Pendiente',  bg: 'warn.bg',   color: 'warn.500'   },
  confirmed: { label: 'Confirmada', bg: 'sky.bg',    color: 'sky.ink'    },
  completed: { label: 'Completada', bg: 'ok.bg',     color: 'ok.500'     },
  cancelled: { label: 'Cancelada',  bg: 'danger.bg', color: 'danger.500' },
};

const studentProfileUrl = (wpUserId) => {
  if (!wpUserId) return null;
  return `${window.location.href.split('#')[0]}#/admin/usuario/${wpUserId}`;
};

// Pill reutilizable estilo Datofy V1
const Pill = ({ bg, color, children, ...rest }) => (
  <Box
    as="span"
    display="inline-flex"
    alignItems="center"
    gap="4px"
    px="9px"
    py="3px"
    borderRadius="999px"
    bg={bg}
    color={color}
    fontSize="11.5px"
    fontWeight="700"
    {...rest}
  >
    {children}
  </Box>
);

// Busca por código con highlight
function CodeHighlight({ code, search }) {
  if (!search || !code) return <Text as="span" fontFamily="mono" fontWeight="700" color="coral.700">#{code}</Text>;
  const idx = code.toUpperCase().indexOf(search.toUpperCase());
  if (idx === -1) return <Text as="span" fontFamily="mono" fontWeight="700" color="coral.700">#{code}</Text>;
  return (
    <Text as="span" fontFamily="mono" fontWeight="700" color="coral.700">
      #{code.slice(0, idx)}
      <Text as="span" bg="butter.bg" color="ink.900" borderRadius="sm" px="1px">{code.slice(idx, idx + search.length)}</Text>
      {code.slice(idx + search.length)}
    </Text>
  );
}

export default function AllAppointmentsAdmin() {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading]           = useState(false);
  const [total, setTotal]               = useState(0);
  const [page, setPage]                 = useState(1);
  const [pages, setPages]               = useState(1);

  // Filtros
  const [searchInput, setSearchInput]   = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [dateFrom, setDateFrom]         = useState('');
  const [dateTo, setDateTo]             = useState('');

  // Expansión de filas en mobile
  const [expandedRow, setExpandedRow]   = useState(null);

  const debounceTimer = useRef(null);
  const toast = useToast();

  const isMobile = useBreakpointValue({ base: true, lg: false });

  // ── Cargar lista general ──
  const loadAppointments = useCallback(async (opts = {}) => {
    setLoading(true);
    try {
      const effectiveSearch = opts.search ?? searchInput;
      const today = new Date().toISOString().split('T')[0];
      // Al buscar (incluye código de confirmación) no se restringe a "desde hoy",
      // así también se encuentran citas pasadas por nombre, email o código.
      const res = await getAllAppointments({
        status:   opts.status   ?? statusFilter,
        dateFrom: opts.dateFrom ?? (dateFrom || (effectiveSearch.trim() ? '' : today)),
        dateTo:   opts.dateTo   ?? dateTo,
        search:   effectiveSearch,
        page:     opts.page     ?? page,
        perPage:  50,
      });
      if (res.success) {
        setAppointments(res.data || []);
        setTotal(res.total || 0);
        setPages(res.pages || 1);
      }
    } catch (err) {
      toast({ title: 'Error', description: err.message, status: 'error', duration: 3000, isClosable: true });
    } finally {
      setLoading(false);
    }
  }, [statusFilter, dateFrom, dateTo, searchInput, page, toast]);

  useEffect(() => { loadAppointments(); }, [statusFilter, dateFrom, dateTo, page]);

  // Debounce búsqueda libre
  useEffect(() => {
    clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      setPage(1);
      loadAppointments({ search: searchInput, page: 1 });
    }, 400);
    return () => clearTimeout(debounceTimer.current);
  }, [searchInput]);

  const formatDate = (date, time) => {
    const dt = new Date(`${date}T${time}`);
    return {
      date: dt.toLocaleDateString('es-CL', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' }),
      time: dt.toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit', hour12: true }),
    };
  };

  const formatCreatedAt = (createdAt) => {
    if (!createdAt) return '—';
    const d = new Date(createdAt);
    if (isNaN(d)) return createdAt;
    return d.toLocaleString('es-CL', {
      day: '2-digit', month: '2-digit', year: '2-digit',
      hour: '2-digit', minute: '2-digit', hour12: false,
    });
  };

  const isToday  = (d) => d === new Date().toISOString().split('T')[0];
  const isPast   = (d, t) => new Date(`${d}T${t}`) < new Date();

  // ── Tarjeta de cita (código) ──
  const AppointmentCard = ({ apt, highlight }) => {
    const st      = APPT_STATUS[apt.status] || { label: apt.status, bg: 'cream.bg2', color: 'ink.400' };
    const dt      = formatDate(apt.appointment_date, apt.start_time);
    const today   = isToday(apt.appointment_date);
    const past    = isPast(apt.appointment_date, apt.start_time);
    const initials = (apt.student_name || apt.guardian_name || '?')
      .split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();

    return (
      <Box bg="white" border="1px solid" borderColor={today ? 'coral.soft' : 'cream.line'}
        borderRadius="14px" p={4} boxShadow={today ? '0 4px 12px rgba(54,38,22,.1)' : '0 1px 2px rgba(54,38,22,.05)'}>
        {/* Header */}
        <Flex justify="space-between" align="center" mb={3}>
          <HStack spacing={2}>
            <Pill bg={st.bg} color={st.color}>{st.label}</Pill>
            {today && <Pill bg="sky.bg" color="sky.ink">Hoy</Pill>}
            {past && apt.status === 'pending' && <Pill bg="cream.bg2" color="ink.400">Pasada</Pill>}
          </HStack>
          <CodeHighlight code={apt.confirmation_code} search={highlight} />
        </Flex>

        <SimpleGrid columns={2} spacing={3}>
          {/* Fecha / hora */}
          <Box>
            <HStack spacing={1} mb={1}><Icon as={FiCalendar} color="sky.ink" boxSize="12px" /><Text fontSize="11px" fontWeight="800" color="ink.400" textTransform="uppercase" letterSpacing="0.04em">Fecha</Text></HStack>
            <Text fontSize="sm" fontWeight="700" color="ink.900">{dt.date}</Text>
            <HStack spacing={1} mt={1}><Icon as={FiClock} color="lav.ink" boxSize="12px" /><Text fontSize="sm" color="ink.400">{dt.time} – {formatDate(apt.appointment_date, apt.end_time).time}</Text></HStack>
          </Box>

          {/* Profesor */}
          <Box>
            <Text fontSize="11px" fontWeight="800" color="ink.400" textTransform="uppercase" letterSpacing="0.04em" mb={1}>Profesor</Text>
            <Text fontSize="sm" fontWeight="700" color="ink.900">{apt.teacher_name || `#${apt.teacher_id}`}</Text>
          </Box>

          {/* Alumno */}
          <Box>
            <Text fontSize="11px" fontWeight="800" color="ink.400" textTransform="uppercase" letterSpacing="0.04em" mb={1}>Alumno</Text>
            <HStack spacing={2}>
              <Circle size="28px" bg="coral.500" color="white" fontSize="xs" fontWeight="700">{initials}</Circle>
              <VStack align="start" spacing={0}>
                <Text fontSize="sm" fontWeight="700" color="ink.900">{apt.student_name || apt.student_rut}</Text>
                <Text fontSize="xs" color="ink.400">{apt.student_rut}</Text>
              </VStack>
            </HStack>
            {apt.student_id && (
              <Button as="a" href={studentProfileUrl(apt.student_id)} target="_blank"
                size="xs" variant="link" color="coral.600" leftIcon={<Icon as={FiExternalLink} boxSize="11px" />} mt={1}>
                Ver perfil
              </Button>
            )}
          </Box>

          {/* Apoderado */}
          <Box>
            <Text fontSize="11px" fontWeight="800" color="ink.400" textTransform="uppercase" letterSpacing="0.04em" mb={1}>Apoderado</Text>
            <Text fontSize="sm" color="ink.900">{apt.guardian_name}</Text>
            <HStack spacing={1} mt={1}><Icon as={FiMail} boxSize="12px" color="ink.400" /><Text fontSize="xs" color="ink.400">{apt.guardian_email}</Text></HStack>
            {apt.guardian_phone && <HStack spacing={1} mt={1}><Icon as={FiPhone} boxSize="12px" color="ink.400" /><Text fontSize="xs" color="ink.400">{apt.guardian_phone}</Text></HStack>}
          </Box>
        </SimpleGrid>

        {apt.notes && (
          <Box mt={3} p={3} bg="cream.bg2" borderRadius="11px">
            <Text fontSize="11px" fontWeight="800" color="ink.400" textTransform="uppercase" letterSpacing="0.04em" mb={1}>Notas</Text>
            <Text fontSize="sm" color="ink.900">{apt.notes}</Text>
          </Box>
        )}

        {apt.created_at && (
          <HStack spacing={1} mt={3}>
            <Icon as={FiClock} boxSize="11px" color="ink.400" />
            <Text fontSize="xs" color="ink.400">
              Registrada: {formatCreatedAt(apt.created_at)}
            </Text>
          </HStack>
        )}
      </Box>
    );
  };

  // ── Fila de tabla (desktop) ──
  const TableRow = ({ apt }) => {
    const st         = APPT_STATUS[apt.status] || { label: apt.status, bg: 'cream.bg2', color: 'ink.400' };
    const dt         = formatDate(apt.appointment_date, apt.start_time);
    const today      = isToday(apt.appointment_date);
    const past       = isPast(apt.appointment_date, apt.start_time);
    const isExpanded = expandedRow === apt.id;

    return (
      <>
        <Tr
          _hover={{ bg: 'cream.bg2' }}
          bg={today ? 'sky.bg' : 'transparent'}
          cursor="pointer"
          onClick={() => setExpandedRow(isExpanded ? null : apt.id)}
        >
          <Td py={2}>
            <CodeHighlight code={apt.confirmation_code} search={searchInput} />
          </Td>
          <Td py={2}>
            <HStack spacing={1}>
              <Pill bg={st.bg} color={st.color}>{st.label}</Pill>
              {today && <Pill bg="sky.bg" color="sky.ink">Hoy</Pill>}
              {past && apt.status === 'pending' && <Pill bg="cream.bg2" color="ink.400">Pasada</Pill>}
            </HStack>
          </Td>
          <Td py={2}>
            <Text fontSize="sm" fontWeight="700" color="ink.900">{dt.date}</Text>
            <Text fontSize="xs" color="ink.400">{dt.time}</Text>
          </Td>
          <Td py={2}>
            <Text fontSize="sm" color="ink.900">{apt.teacher_name || `#${apt.teacher_id}`}</Text>
          </Td>
          <Td py={2}>
            <Text fontSize="sm" fontWeight="600" color="ink.900">{apt.student_name || apt.student_rut}</Text>
            <Text fontSize="xs" color="ink.400">{apt.student_rut}</Text>
          </Td>
          <Td py={2}>
            <Text fontSize="sm" color="ink.900">{apt.guardian_name}</Text>
            <Text fontSize="xs" color="ink.400">{apt.guardian_email}</Text>
          </Td>
          <Td py={2}>
            <Text fontSize="xs" color="ink.400" whiteSpace="nowrap">
              {formatCreatedAt(apt.created_at)}
            </Text>
          </Td>
          <Td py={2} isNumeric>
            <IconButton
              icon={<Icon as={isExpanded ? FiChevronUp : FiChevronDown} />}
              size="xs" variant="ghost" color="ink.400" aria-label="Expandir"
              _hover={{ bg: 'cream.bg2', color: 'coral.500' }}
              onClick={(e) => { e.stopPropagation(); setExpandedRow(isExpanded ? null : apt.id); }}
            />
          </Td>
        </Tr>
        {isExpanded && (
          <Tr>
            <Td colSpan={8} p={0} bg="cream.bg2">
              <Box p={4}>
                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                  {apt.notes && (
                    <Box>
                      <Text fontSize="11px" fontWeight="800" color="ink.400" textTransform="uppercase" letterSpacing="0.04em" mb={1}>Notas del apoderado</Text>
                      <Text fontSize="sm" color="ink.900">{apt.notes}</Text>
                    </Box>
                  )}
                  {apt.teacher_notes && (
                    <Box>
                      <Text fontSize="11px" fontWeight="800" color="ink.400" textTransform="uppercase" letterSpacing="0.04em" mb={1}>Notas del profesor</Text>
                      <Text fontSize="sm" color="ink.900">{apt.teacher_notes}</Text>
                    </Box>
                  )}
                  {apt.cancel_reason && (
                    <Box>
                      <Text fontSize="11px" fontWeight="800" color="danger.500" textTransform="uppercase" letterSpacing="0.04em" mb={1}>Motivo cancelación</Text>
                      <Text fontSize="sm" color="ink.900">{apt.cancel_reason}</Text>
                    </Box>
                  )}
                  {apt.guardian_phone && (
                    <Box>
                      <Text fontSize="11px" fontWeight="800" color="ink.400" textTransform="uppercase" letterSpacing="0.04em" mb={1}>Teléfono</Text>
                      <Text fontSize="sm" color="ink.900">{apt.guardian_phone}</Text>
                    </Box>
                  )}
                  {apt.student_id && (
                    <Box>
                      <Button as="a" href={studentProfileUrl(apt.student_id)} target="_blank"
                        size="sm" variant="outline" borderColor="coral.500" color="coral.600"
                        _hover={{ bg: 'coral.tint' }} leftIcon={<Icon as={FiExternalLink} boxSize="13px" />}>
                        Ver perfil alumno
                      </Button>
                    </Box>
                  )}
                  {apt.updated_at && apt.updated_at !== apt.created_at && (
                    <Box>
                      <Text fontSize="11px" fontWeight="800" color="ink.400" textTransform="uppercase" letterSpacing="0.04em" mb={1}>Última actualización</Text>
                      <Text fontSize="sm" color="ink.900">{formatCreatedAt(apt.updated_at)}</Text>
                    </Box>
                  )}
                </SimpleGrid>
              </Box>
            </Td>
          </Tr>
        )}
      </>
    );
  };

  // ── Resetear filtros ──
  const hasFilters = statusFilter || dateFrom || dateTo || searchInput;
  const resetFilters = () => {
    setStatusFilter('');
    setDateFrom('');
    setDateTo('');
    setSearchInput('');
    setPage(1);
  };

  return (
    <VStack spacing={4} align="stretch" fontFamily="jakarta">

      {/* ── FILTROS LISTA GENERAL ── */}
      <Box bg="white" border="1px solid" borderColor="cream.line" borderRadius="18px" p={4} boxShadow="0 4px 6px -1px rgba(43,39,35,0.06)">
        <Flex justify="space-between" align="center" mb={3}>
          <Text fontSize="sm" fontWeight="800" color="ink.900">Todas las citas actuales</Text>
          {hasFilters && (
            <Button size="xs" variant="ghost" color="danger.500" _hover={{ bg: 'danger.bg' }} onClick={resetFilters}>
              Limpiar filtros
            </Button>
          )}
        </Flex>

        <SimpleGrid columns={{ base: 1, sm: 2, md: 4 }} spacing={3} mb={4}>
          <InputGroup>
            <InputLeftElement pointerEvents="none">
              <Icon as={FiSearch} color="ink.400" boxSize="14px" />
            </InputLeftElement>
            <Input
              placeholder="Buscar nombre, email o código..."
              value={searchInput}
              onChange={(e) => { setSearchInput(e.target.value); setPage(1); }}
              size="sm"
              bg="white"
              focusBorderColor="coral.500"
            />
            {searchInput && (
              <InputRightElement>
                <IconButton icon={<Icon as={FiX} boxSize="11px" />} size="xs" variant="ghost" color="ink.400"
                  onClick={() => { setSearchInput(''); setPage(1); }} aria-label="Limpiar" />
              </InputRightElement>
            )}
          </InputGroup>

          <Select
            placeholder="Todos los estados"
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
            size="sm"
            bg="white"
            focusBorderColor="coral.500"
          >
            {Object.entries(APPT_STATUS).map(([k, v]) => (
              <option key={k} value={k}>{v.label}</option>
            ))}
          </Select>

          <Input
            type="date"
            placeholder="Desde"
            value={dateFrom}
            onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
            size="sm"
            bg="white"
            focusBorderColor="coral.500"
          />

          <Input
            type="date"
            placeholder="Hasta"
            value={dateTo}
            onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
            size="sm"
            bg="white"
            focusBorderColor="coral.500"
          />
        </SimpleGrid>

        {/* Contador */}
        <HStack spacing={2} mb={3}>
          <Pill bg="teal.bg" color="teal.ink">{total} citas encontradas</Pill>
          {loading && <Spinner size="xs" color="coral.500" />}
        </HStack>

        {/* Tabla desktop */}
        {!isMobile ? (
          loading && appointments.length === 0 ? (
            <Center py={10}><Spinner size="lg" color="coral.500" /></Center>
          ) : appointments.length === 0 ? (
            <Center py={10}>
              <Text color="ink.400" fontSize="sm">No hay citas para los filtros seleccionados.</Text>
            </Center>
          ) : (
            <Box overflowX="auto">
              <Table size="sm" variant="simple">
                <Thead>
                  <Tr>
                    <Th color="ink.400">Código</Th>
                    <Th color="ink.400">Estado</Th>
                    <Th color="ink.400">Fecha / Hora</Th>
                    <Th color="ink.400">Profesor</Th>
                    <Th color="ink.400">Alumno</Th>
                    <Th color="ink.400">Apoderado</Th>
                    <Th color="ink.400">Registrada</Th>
                    <Th />
                  </Tr>
                </Thead>
                <Tbody>
                  {appointments.map((apt) => <TableRow key={apt.id} apt={apt} />)}
                </Tbody>
              </Table>
            </Box>
          )
        ) : (
          /* Lista mobile */
          loading && appointments.length === 0 ? (
            <Center py={10}><Spinner size="lg" color="coral.500" /></Center>
          ) : appointments.length === 0 ? (
            <Center py={10}>
              <Text color="ink.400" fontSize="sm">No hay citas para los filtros seleccionados.</Text>
            </Center>
          ) : (
            <VStack spacing={3} align="stretch">
              {appointments.map((apt) => <AppointmentCard key={apt.id} apt={apt} highlight={searchInput} />)}
            </VStack>
          )
        )}

        {/* Paginación */}
        {pages > 1 && (
          <HStack justify="center" mt={4} spacing={2}>
            <Button size="sm" variant="outline" borderColor="cream.line2" color="ink.700"
              onClick={() => setPage(p => Math.max(1, p - 1))} isDisabled={page <= 1}>
              Anterior
            </Button>
            <Text fontSize="sm" color="ink.400">Página {page} de {pages}</Text>
            <Button size="sm" variant="outline" borderColor="cream.line2" color="ink.700"
              onClick={() => setPage(p => Math.min(pages, p + 1))} isDisabled={page >= pages}>
              Siguiente
            </Button>
          </HStack>
        )}
      </Box>
    </VStack>
  );
}
